import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Cancel";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import api from "../../services/api";
import { useLocationCtx } from "../../context/LocationContext";
import { userHasActiveEntitlement } from "../../utils/entitlements";
import { getCapabilities } from "../../utils/capabilities";
import { trackEvent } from "../../utils/analytics/gtag";
import { getPremiumPricesByCountry } from "../../utils/premiumPricing";

// ίδια utilities που ήδη χρησιμοποιείς στο SubscriptionPage
import {
    getCurrencyByCountryId,
    getCurrencySymbol,
} from "../utils/getCurrencyByCountry";

function safeNumber(v) {
    if (v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function formatMoney(amountDecimalStringOrNumber, currency) {
    if (amountDecimalStringOrNumber === null || amountDecimalStringOrNumber === undefined || amountDecimalStringOrNumber === "") {
        return "--";
    }
    const raw = String(amountDecimalStringOrNumber).trim();
    if (!raw) return "--";
    const num = Number(raw);
    if (!Number.isFinite(num)) return "--";

    const normalizedCurrency = String(currency || "").toUpperCase();
    const symbol = normalizedCurrency === "USD" ? "$" : "€";
    const decimals = raw.includes(".") ? raw.split(".")[1].length : 2;

    return `${symbol}${num.toFixed(Math.max(decimals, 2))}`;
}

export default function PricingPage() {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const navigate = useNavigate();
    const routerLocation = useLocation();

    // OPTIONAL: αν έχεις route /:country/pricing -> παίρνουμε lang
    const { country: langFromParams } = useParams();

    // Αν έχεις LocationContext (όπως στο δικό σου Pricing), το κρατάμε
    const { country } = useLocationCtx();

    // prefix (/el, /en, etc)
    const match = routerLocation.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\b/);
    const prefix = match ? `/${match[1]}` : "";

    // Mapping lang->countryId (ίδιο με SubscriptionPage για συνέπεια)
    const langToCountryId = useMemo(
        () => ({
            el: 1, // Ελλάδα
            gr: 1, // Greece (region-style route)
            en: 2, // ΗΠΑ
            us: 2, // USA (region-style route)
            ro: 3,
            it: 4,
            es: 5,
        }),
        []
    );

    // Αν θες να “κερδίζει” το LocationContext: κράτα αυτό.
    // Αν θες να κερδίζει πάντα το lang route param: άλλαξε τη σειρά.
    const countryId = useMemo(() => {
        if (country !== undefined && country !== null) return Number(country) || 1;
        const l = (langFromParams || "").toLowerCase();
        return langToCountryId[l] || 1;
    }, [country, langFromParams, langToCountryId]);

    /* ---------------- user ---------------- */

    const [user, setUser] = useState(null);
    const [checkingUser, setCheckingUser] = useState(true);

    useEffect(() => {
        api.users
            .getCurrentUser()
            .then((res) => {
                const u = res.data?.data || res.data?.user || res.data;
                setUser(u || null);
            })
            .catch(() => setUser(null))
            .finally(() => setCheckingUser(false));
    }, []);

    const isLoggedIn = !!user;
    const isPremium = userHasActiveEntitlement(user);
    const userTier = isPremium ? "premium" : isLoggedIn ? "free" : "guest";
    const { caps } = useMemo(() => getCapabilities({ user }), [user]);
    const funnelIntent = (new URLSearchParams(routerLocation.search || "").get("intent") || "").trim().toLowerCase() === "premium"
        ? "premium"
        : null;
    const routeLang = (langFromParams || match?.[1] || "en").toLowerCase();
    const funnelCountry = routeLang;

    /* ---------------- currency + stripe prices ---------------- */

    const [currency, setCurrency] = useState("eur");
    const [symbol, setSymbol] = useState("€");
    const [currencyDefault, setCurrencyDefault] = useState("eur");
    const [pricingCatalog, setPricingCatalog] = useState([]);
    const [loadingPrices, setLoadingPrices] = useState(true);
    const [pricesError, setPricesError] = useState("");
    const isDev = process.env.NODE_ENV !== "production";

    useEffect(() => {
        const curr = getCurrencyByCountryId(countryId);
        setCurrency(curr);
        setSymbol(getCurrencySymbol(curr));
    }, [countryId]);

    const queryCurrencyOverride = useMemo(() => {
        if (!isDev) return null;
        const value = (new URLSearchParams(routerLocation.search).get("currency") || "").trim().toLowerCase();
        return value === "eur" || value === "usd" ? value : null;
    }, [isDev, routerLocation.search]);

    const intentPlanFromQuery = useMemo(() => {
        const raw = (new URLSearchParams(routerLocation.search).get("plan") || "").trim().toLowerCase();
        return raw === "yearly" ? "yearly" : "monthly";
    }, [routerLocation.search]);

    useEffect(() => {
        let mounted = true;
        setLoadingPrices(true);
        setPricesError("");
        setPricingCatalog([]);

        api.public
            .getPricingCatalog(countryId)
            .then((res) => {
                if (!mounted) return;
                const plans = Array.isArray(res?.data?.plans) ? res.data.plans : [];
                const fallbackCurrency = String(res?.data?.currency_default || "eur").toLowerCase();
                setCurrencyDefault(fallbackCurrency === "usd" ? "usd" : "eur");
                setPricingCatalog(plans);
            })
            .catch((err) => {
                if (!mounted) return;
                console.error("❌ Stripe prices error:", err);
                setPricesError(t("pricing.prices_error"));
                setPricingCatalog([]);
            })
            .finally(() => mounted && setLoadingPrices(false));

        return () => {
            mounted = false;
        };
    }, [countryId, t]);

    const selectedCurrency = queryCurrencyOverride || currency;
    const effectiveCurrency = useMemo(() => {
        if (pricingCatalog.some((p) => String(p?.currency || "").toLowerCase() === selectedCurrency)) {
            return selectedCurrency;
        }
        if (pricingCatalog.some((p) => String(p?.currency || "").toLowerCase() === currencyDefault)) {
            return currencyDefault;
        }
        return selectedCurrency;
    }, [pricingCatalog, selectedCurrency, currencyDefault]);

    useEffect(() => {
        trackEvent("funnel_subscription_view", {
            lang: routeLang,
            country: funnelCountry,
            plan: intentPlanFromQuery,
            currency: effectiveCurrency,
            intent: funnelIntent,
            user_tier: userTier,
        });
    }, [routeLang, funnelCountry, intentPlanFromQuery, effectiveCurrency, funnelIntent, userTier]);

    const findPlan = (plan) => (
        pricingCatalog.find(
            (p) => p?.plan === plan && String(p?.currency || "").toLowerCase() === effectiveCurrency
        ) || null
    );
    const MONTHLY_PLAN = findPlan("monthly");
    const YEARLY_PLAN = findPlan("yearly");
    const MONTHLY_PRICE = safeNumber(MONTHLY_PLAN?.amount_decimal);
    const YEARLY_PRICE = safeNumber(YEARLY_PLAN?.amount_decimal);
    const YEARLY_MONTHLY_EQUIVALENT = YEARLY_PRICE ? (YEARLY_PRICE / 12).toFixed(2) : "--";
    const fallbackPremiumPricing = getPremiumPricesByCountry(countryId);

    const premiumMainPriceLabel = MONTHLY_PRICE
        ? `${MONTHLY_PLAN.amount_decimal} ${String(MONTHLY_PLAN.currency || "").toUpperCase()} / ${MONTHLY_PLAN.interval}`
        : t(
            fallbackPremiumPricing.isUS ? "pricing.premium_us_monthly" : "pricing.premium_eu_monthly"
        );

    const premiumSecondaryPriceLabel = YEARLY_PRICE
        ? `${YEARLY_PLAN.amount_decimal} ${String(YEARLY_PLAN.currency || "").toUpperCase()} / ${YEARLY_PLAN.interval}`
        : `${t(
            fallbackPremiumPricing.isUS ? "pricing.premium_us_yearly" : "pricing.premium_eu_yearly"
        )} ${t(
            fallbackPremiumPricing.isUS
                ? "pricing.premium_us_yearly_equivalent"
                : "pricing.premium_eu_yearly_equivalent"
        )}`;

    const monthlyBannerLabel = MONTHLY_PLAN
        ? `${formatMoney(MONTHLY_PLAN.amount_decimal, MONTHLY_PLAN.currency)} / ${t("pricing.per_month_short")}`
        : "--";
    const yearlyBannerLabel = YEARLY_PLAN
        ? `${formatMoney(YEARLY_PLAN.amount_decimal, YEARLY_PLAN.currency)} / ${t("pricing.per_year_short")} (${t("pricing.only")} ${formatMoney(YEARLY_MONTHLY_EQUIVALENT, YEARLY_PLAN.currency)} / ${t("pricing.per_month_short")})`
        : null;

    /* ---------------- PlanCard ---------------- */

    const PlanCard = ({
        title,
        priceMain,
        priceSecondary,
        features,
        highlight,
        buttonLabel,
        onButtonClick,
        disabled,
    }) => {
        const border = highlight
            ? `2px solid ${theme.palette.success.main}`
            : `1px solid ${theme.palette.divider}`;

        const bg = highlight
            ? isDark
                ? "linear-gradient(135deg, rgba(56,142,60,.3), rgba(27,94,32,.7))"
                : "linear-gradient(135deg, rgba(76,175,80,.08), rgba(129,199,132,.25))"
            : theme.palette.background.paper;

        return (
            <Paper
                elevation={highlight ? 8 : 2}
                sx={{
                    p: 3,
                    borderRadius: 3,
                    border,
                    background: bg,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {highlight && (
                    <Chip
                        label={t("pricing.most_popular")}
                        color="success"
                        size="small"
                        sx={{ mb: 2, fontWeight: 700 }}
                    />
                )}

                <Typography variant="h5" fontWeight={700}>
                    {title}
                </Typography>

                <Typography variant="h4" fontWeight={800} my={1}>
                    {priceMain}
                </Typography>

                {priceSecondary && (
                    <Typography variant="subtitle2" color="text.secondary">
                        {priceSecondary}
                    </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                <List dense sx={{ flexGrow: 1 }}>
                    {features.map((f, i) => (
                        <ListItem key={i} sx={{ py: 0.4 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <Box
                                    sx={{
                                        width: 22,
                                        height: 22,
                                        borderRadius: "50%",
                                        display: "grid",
                                        placeItems: "center",
                                        bgcolor: f.yes ? "rgba(0, 200, 83, 0.12)" : "rgba(244, 67, 54, 0.12)",
                                    }}
                                >
                                    {f.yes ? (
                                        <CheckIcon sx={{ fontSize: 18, color: "success.main" }} />
                                    ) : (
                                        <CloseIcon sx={{ fontSize: 18, color: "error.main" }} />
                                    )}
                                </Box>

                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography variant="body2">
                                        {t(`pricing.features.${f.key}`)}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    ))}
                </List>

                <Button
                    variant={highlight ? "contained" : "outlined"}
                    color="success"
                    fullWidth
                    disabled={disabled}
                    sx={{ mt: 2, py: 1.2, fontWeight: 600 }}
                    onClick={onButtonClick}
                >
                    {buttonLabel}
                </Button>
            </Paper>
        );
    };

    /* ---------------- CTA ---------------- */

    const goRegister = () => navigate(`${prefix}/register`);
    const goLogin = () => navigate(`${prefix}/login`);
    const goSubscription = (plan = null) => {
        const planNorm = plan === "yearly" ? "yearly" : "monthly";
        trackEvent("subscribe_click", {
            plan: "premium",
            billing_period: planNorm === "yearly" ? "year" : "month",
            currency: effectiveCurrency ? effectiveCurrency.toUpperCase() : null,
            region: String(funnelCountry || "").toUpperCase(),
            is_guest: !isLoggedIn,
        });
        trackEvent("funnel_checkout_click", {
            lang: routeLang,
            country: funnelCountry,
            plan: planNorm,
            currency: effectiveCurrency,
            intent: "premium",
            user_tier: userTier,
        });
        if (plan) {
            navigate(`${prefix}/subscription?intent=premium&plan=${planNorm}`);
            return;
        }
        navigate(`${prefix}/subscription`);
    };
    const goLoginPremium = (plan = intentPlanFromQuery) => {
        const planNorm = plan === "yearly" ? "yearly" : "monthly";
        trackEvent("subscribe_click", {
            plan: "premium",
            billing_period: planNorm === "yearly" ? "year" : "month",
            currency: effectiveCurrency ? effectiveCurrency.toUpperCase() : null,
            region: String(funnelCountry || "").toUpperCase(),
            is_guest: !isLoggedIn,
        });
        trackEvent("funnel_checkout_click", {
            lang: routeLang,
            country: funnelCountry,
            plan: planNorm,
            currency: effectiveCurrency,
            intent: "premium",
            user_tier: userTier,
        });
        const returnTo = `${prefix}/subscription?intent=premium&plan=${planNorm}`;
        const qs = new URLSearchParams({
            intent: "premium",
            plan: planNorm,
            returnTo,
        });
        navigate(`${prefix}/login?${qs.toString()}`);
    };

    /* ---------------- render ---------------- */

    return (
        <Box sx={{ p: { xs: 2.5, md: 4 }, maxWidth: 1200, mx: "auto" }}>
            <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
                {t("pricing.title")}
            </Typography>

            <Typography align="center" color="text.secondary" mb={3}>
                {t("pricing.subtitle")}
            </Typography>

            {/* feedback για loading/prices */}
            {loadingPrices && (
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}

            {pricesError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {pricesError}
                </Alert>
            )}

            {/* info box με real prices όταν έρθουν */}
            <Alert
                severity="info"
                sx={{
                    mb: 3,
                    bgcolor: (th) => (th.palette.mode === "dark" ? "#1c2530" : "#e3f2fd"),
                    color: (th) => th.palette.text.primary,
                    borderRadius: 2,
                    boxShadow: 1,
                }}
            >
                {loadingPrices ? (
                    t("pricing.loading_prices")
                ) : (
                    <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                            {t("pricing.premium_pro")}: {monthlyBannerLabel}
                        </Typography>
                        {yearlyBannerLabel ? (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {t("pricing.or")} {yearlyBannerLabel}
                            </Typography>
                        ) : null}
                    </Box>
                )}
            </Alert>

            {!checkingUser && isPremium && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {t("pricing.already_premium")}
                </Alert>
            )}

            <Grid container spacing={3} justifyContent="center">
                <Grid item xs={12} md={4}>
                    <PlanCard
                        title={t("pricing.guest")}
                        priceMain={t("pricing.free")}
                        priceSecondary={t("pricing.no_account")}
                        features={caps.pricing.guest}
                        buttonLabel={t("pricing.create_account")}
                        onButtonClick={goRegister}
                        disabled={false}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <PlanCard
                        title={t("pricing.free_user")}
                        priceMain={`${symbol}0`}
                        priceSecondary={t("pricing.with_account")}
                        features={caps.pricing.free}
                        buttonLabel={
                            checkingUser
                                ? t("common.loading")
                                : isLoggedIn
                                    ? t("pricing.upgrade")
                                    : t("pricing.login")
                        }
                        onButtonClick={isLoggedIn ? goSubscription : goLogin}
                        disabled={checkingUser}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <PlanCard
                        title={t("pricing.premium")}
                        priceMain={premiumMainPriceLabel}
                        priceSecondary={premiumSecondaryPriceLabel}
                        features={caps.pricing.premium}
                        highlight
                        buttonLabel={
                            checkingUser
                                ? t("common.loading")
                                : isPremium
                                    ? t("pricing.manage")
                                    : t("pricing.upgrade")
                        }
                        onButtonClick={
                            isPremium
                                ? () => goSubscription()
                                : isLoggedIn
                                    ? () => goSubscription(intentPlanFromQuery)
                                    : () => goLoginPremium(intentPlanFromQuery)
                        }
                        disabled={checkingUser || loadingPrices || !MONTHLY_PRICE}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}
